/**
 * ELEMENTAL API Client
 * Drop this in src/services/api.js
 */

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// =============================================================================
// HTTP HELPERS
// =============================================================================

function getToken() {
  return localStorage.getItem('token');
}

export function setToken(token) {
  if (token) {
    localStorage.setItem('token', token);
  } else {
    localStorage.removeItem('token');
  }
}

async function request(endpoint, options = {}) {
  const token = getToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API}${endpoint}`, { ...options, headers });
  const data = await res.json();
  
  if (!res.ok) {
    throw new Error(data.error || 'Request failed');
  }
  
  return data;
}

// =============================================================================
// AUTH
// =============================================================================

export const Auth = {
  async signup(email, password, name) {
    const data = await request('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, name })
    });
    setToken(data.token);
    return data.user;
  },

  async login(email, password) {
  const data = await request('/api/auth/login', {  // ← Must have /api
    method: 'POST',
    body: JSON.stringify({ email, password })
  });
  setToken(data.token);
  return data.user;
},

  async me() {
    const data = await request('/api/auth/me');
    return data.user;
  },

  logout() {
    setToken(null);
  },

  isLoggedIn() {
    return !!getToken();
  }
};

// =============================================================================
// STOCKS
// =============================================================================

export const Stocks = {
  async quote(symbol) {
    return await request(`/api/stocks/quote/${symbol}`);
  },

  async overview(symbol) {
    return await request(`/api/stocks/overview/${symbol}`);
  },

  async history(symbol, range = '1Y') {
    return await request(`/api/stocks/history/${symbol}?range=${range}`);
  },

  async search(query) {
    const data = await request(`/api/stocks/search?q=${encodeURIComponent(query)}`);
    return data.results;
  },

  async news(symbol) {
    return await request(`/api/stocks/news/${symbol}`);
  }
};

// =============================================================================
// WATCHLISTS
// =============================================================================

export const Watchlists = {
  async getAll() {
    const data = await request('/api/watchlists');
    return data.watchlists;
  },

  async create(name) {
    return await request('/api/watchlists', {
      method: 'POST',
      body: JSON.stringify({ name })
    });
  },

  async update(id, { name, symbols }) {
    return await request(`/api/watchlists/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name, symbols })
    });
  },

  async delete(id) {
    return await request(`/api/watchlists/${id}`, { method: 'DELETE' });
  },

  async addSymbol(id, symbol, currentSymbols) {
    const symbols = [...currentSymbols, symbol.toUpperCase()];
    await this.update(id, { symbols });
    return symbols;
  },

  async removeSymbol(id, symbol, currentSymbols) {
    const symbols = currentSymbols.filter(s => s !== symbol.toUpperCase());
    await this.update(id, { symbols });
    return symbols;
  }
};

// =============================================================================
// ALERTS
// =============================================================================

export const Alerts = {
  async getAll() {
    const data = await request('/api/alerts');
    return data.alerts;
  },

  async create({ symbol, type, condition, targetValue }) {
    return await request('/api/alerts', {
      method: 'POST',
      body: JSON.stringify({ symbol, type, condition, targetValue })
    });
  },

  async delete(id) {
    return await request(`/api/alerts/${id}`, { method: 'DELETE' });
  }
};

// =============================================================================
// AI ANALYSIS
// =============================================================================

export const Analysis = {
  async analyze(symbol, data = {}) {
    return await request('/api/analyze', {
      method: 'POST',
      body: JSON.stringify({ symbol, data })
    });
  }
};

// =============================================================================
// PAYMENTS
// =============================================================================

export const Payments = {
  // Redirect user to Stripe Checkout
  async createCheckout(plan) {
    const data = await request('/api/stripe/create-checkout', {
      method: 'POST',
      body: JSON.stringify({ plan })
    });
    // Redirect to Stripe
    if (data.url) {
      window.location.href = data.url;
    }
    return data;
  },

  // Open Stripe billing portal (manage subscription, cancel, update payment)
  async openPortal() {
    const data = await request('/api/stripe/portal', {
      method: 'POST'
    });
    if (data.url) {
      window.location.href = data.url;
    }
    return data;
  },

  // Get current subscription status
  async getSubscription() {
    return await request('/api/stripe/subscription');
  }
};

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default { Auth, Stocks, Watchlists, Alerts, Analysis, Payments, setToken };